import {
  configMgmtControllerListDocumentTypes,
  documentsControllerDelete,
  documentsControllerFindAll,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Eye, Trash2, Upload, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../../components/ui/button';
import { DataTable, type DataTableColumn } from '../../../../components/ui/data-table';
import { FileDropZone } from '../../../../components/ui/file-drop-zone';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { api } from '../../../../lib/ky';
import type { PermitDetail } from './types';

interface Props {
  permit: PermitDetail;
  onUpdated: () => void;
  can: (perm: string) => boolean;
}

interface DocTypeLink {
  id: string;
  documentTypeId: string;
  documentType: { id: string; name: string; code: string };
  requestAction?: string | null;
  status: string;
}

interface DocumentWithUrl {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  createdAt: string;
  signedUrl?: string | null;
  documentType: { id: string; name: string; code: string };
}

const PRE_APPROVAL_STATUSES = [
  'DRAFT',
  'SUBMITTED_FOR_INSPECTION',
  'INSPECTION_IN_PROGRESS',
  'PLEDGE_STUDY_REQUEST',
  'APPROVAL_REQUEST',
  'REJECTED',
];

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function isImage(mimeType: string) {
  return mimeType.startsWith('image/');
}

export function DocumentsTab({ permit, onUpdated, can }: Props) {
  const qc = useQueryClient();

  // upload new document
  const [uploadOpen, setUploadOpen] = useState(false);
  const [docTypeId, setDocTypeId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');

  // replace existing document
  const [replaceDoc, setReplaceDoc] = useState<DocumentWithUrl | null>(null);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replaceFileError, setReplaceFileError] = useState('');

  // delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // preview
  const [previewDoc, setPreviewDoc] = useState<DocumentWithUrl | null>(null);

  const docsKey = ['permitDocs', permit.id];

  const { data: documents, isLoading } = useQuery({
    queryKey: docsKey,
    queryFn: () =>
      documentsControllerFindAll({ permitId: permit.id }) as unknown as Promise<DocumentWithUrl[]>,
  });

  const { data: docTypes } = useQuery({
    queryKey: ['docTypes', permit.requestTypeId],
    queryFn: () =>
      configMgmtControllerListDocumentTypes({ id: permit.requestTypeId }) as unknown as Promise<
        DocTypeLink[]
      >,
    enabled: uploadOpen || !!replaceDoc,
  });

  const canUpload = can('permit:upload-document') && PRE_APPROVAL_STATUSES.includes(permit.status);
  const canDelete = can('permit:delete-document') && PRE_APPROVAL_STATUSES.includes(permit.status);

  const uploadedDocTypeIds = new Set(documents?.map((d) => d.documentType.id) ?? []);

  const filteredDocTypes = docTypes?.filter(
    (l) => l.status === 'ACTIVE' && (!l.requestAction || l.requestAction === permit.requestAction),
  );

  // only show types not yet uploaded in the "new upload" dropdown
  const availableDocTypes = filteredDocTypes?.filter(
    (l) => !uploadedDocTypeIds.has(l.documentTypeId),
  );

  function invalidateDocs() {
    qc.invalidateQueries({ queryKey: docsKey });
    onUpdated();
  }

  const uploadMutation = useMutation({
    mutationFn: async ({ typeId, uploadFile }: { typeId: string; uploadFile: File }) => {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('documentTypeId', typeId);
      await api.post(`permits/${permit.id}/documents`, { body: fd }).json();
    },
    onSuccess: () => {
      invalidateDocs();
      setUploadOpen(false);
      setFile(null);
      setDocTypeId('');
      setFileError('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => documentsControllerDelete({ permitId: permit.id, docId }),
    onSuccess: () => {
      invalidateDocs();
      setDeleteConfirm(null);
    },
  });

  const replaceMutation = useMutation({
    mutationFn: async ({ doc, uploadFile }: { doc: DocumentWithUrl; uploadFile: File }) => {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('documentTypeId', doc.documentType.id);
      await api.post(`permits/${permit.id}/documents`, { body: fd }).json();
      await documentsControllerDelete({ permitId: permit.id, docId: doc.id });
    },
    onSuccess: () => {
      invalidateDocs();
      setReplaceDoc(null);
      setReplaceFile(null);
      setReplaceFileError('');
    },
  });

  const docColumns: DataTableColumn<DocumentWithUrl>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (doc) => <span className="text-gray-700">{doc.documentType.name}</span>,
    },
    {
      key: 'fileName',
      header: 'File',
      render: (doc) =>
        doc.signedUrl ? (
          <a
            href={doc.signedUrl}
            target="_blank"
            rel="noreferrer"
            className="block max-w-48 truncate text-green-600 hover:underline"
          >
            {doc.fileName}
          </a>
        ) : (
          <span className="text-gray-900">{doc.fileName}</span>
        ),
    },
    {
      key: 'size',
      header: 'Size',
      render: (doc) => (
        <span className="text-xs text-gray-500">{formatBytes(doc.fileSizeBytes)}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Uploaded',
      render: (doc) => {
        const d = new Date(doc.createdAt);
        return (
          <span className="text-xs text-gray-500">
            {Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString()}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (doc: DocumentWithUrl) => {
        if (deleteConfirm === doc.id) {
          return (
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                title="Confirm delete"
                onClick={() => deleteMutation.mutate(doc.id)}
                className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-800"
              >
                <Check size={15} />
              </button>
              <button
                type="button"
                title="Cancel"
                onClick={() => setDeleteConfirm(null)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X size={15} />
              </button>
            </div>
          );
        }
        return (
          <div className="flex items-center justify-end gap-1">
            {doc.signedUrl && (
              <button
                type="button"
                title="Preview document"
                onClick={() => setPreviewDoc(doc)}
                className="relative w-8 h-8 shrink-0 overflow-hidden rounded border border-gray-200 bg-gray-50 hover:ring-2 hover:ring-brand-400 transition-all"
              >
                {isImage(doc.mimeType) ? (
                  <img src={doc.signedUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Eye size={14} className="absolute inset-0 m-auto text-gray-400" />
                )}
              </button>
            )}
            {canUpload && (
              <button
                type="button"
                title="Replace document"
                onClick={() => setReplaceDoc(doc)}
                className="rounded p-1 text-gray-400 hover:bg-amber-50 hover:text-amber-600"
              >
                <Upload size={15} />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                title="Delete document"
                onClick={() => setDeleteConfirm(doc.id)}
                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <DataTable
        title={`Documents (${documents?.length ?? 0})`}
        headerActions={
          canUpload ? (
            <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>
              Upload Document
            </Button>
          ) : undefined
        }
        columns={docColumns}
        data={documents}
        keyField="id"
        isLoading={isLoading}
        emptyMessage="No documents uploaded"
      />

      {/* ── Upload new document modal ─────────────────────────────────────── */}
      {uploadOpen && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-foreground/15 backdrop-blur-md"
            onClick={() => {
              setUploadOpen(false);
              setFile(null);
              setDocTypeId('');
              setFileError('');
            }}
          />
          <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">Upload Document</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs text-gray-500">Document Type</label>
                <Select value={docTypeId || undefined} onValueChange={setDocTypeId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDocTypes && availableDocTypes.length > 0 ? (
                      availableDocTypes.map((l) => (
                        <SelectItem key={l.documentTypeId} value={l.documentTypeId}>
                          {l.documentType.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-xs text-gray-400">
                        All document types have been uploaded
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">
                  File (PDF, DOCX, JPG, PNG — max 10 MB)
                </label>
                <FileDropZone
                  file={file}
                  onChange={setFile}
                  onError={setFileError}
                  hint="PDF, DOCX, JPG, PNG — max 10 MB"
                />
                {fileError && <p className="mt-1 text-xs text-red-500">{fileError}</p>}
              </div>
            </div>
            {uploadMutation.isError && (
              <p className="mt-2 text-xs text-red-500">Upload failed. Try again.</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setUploadOpen(false);
                  setFile(null);
                  setDocTypeId('');
                  setFileError('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!file || !docTypeId || !!fileError || uploadMutation.isPending}
                onClick={() => {
                  if (file && docTypeId)
                    uploadMutation.mutate({ typeId: docTypeId, uploadFile: file });
                }}
              >
                {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Replace document modal ────────────────────────────────────────── */}
      {replaceDoc && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-foreground/15 backdrop-blur-md"
            onClick={() => {
              setReplaceDoc(null);
              setReplaceFile(null);
              setReplaceFileError('');
            }}
          />
          <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-1 text-sm font-semibold text-gray-900">Replace Document</h3>
            <p className="mb-4 text-xs text-gray-500">
              Replacing:{' '}
              <span className="font-medium text-gray-700">{replaceDoc.documentType.name}</span>
            </p>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs text-gray-500">
                  New File (PDF, DOCX, JPG, PNG — max 10 MB)
                </label>
                <FileDropZone
                  file={replaceFile}
                  onChange={setReplaceFile}
                  onError={setReplaceFileError}
                  hint="PDF, DOCX, JPG, PNG — max 10 MB"
                />
                {replaceFileError && (
                  <p className="mt-1 text-xs text-red-500">{replaceFileError}</p>
                )}
              </div>
            </div>
            {replaceMutation.isError && (
              <p className="mt-2 text-xs text-red-500">Replace failed. Try again.</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setReplaceDoc(null);
                  setReplaceFile(null);
                  setReplaceFileError('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!replaceFile || !!replaceFileError || replaceMutation.isPending}
                onClick={() => {
                  if (replaceFile && replaceDoc)
                    replaceMutation.mutate({ doc: replaceDoc, uploadFile: replaceFile });
                }}
              >
                {replaceMutation.isPending ? 'Replacing…' : 'Replace'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preview modal ─────────────────────────────────────────────────── */}
      {previewDoc?.signedUrl && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-md"
            onClick={() => setPreviewDoc(null)}
          />
          <div className="relative z-10 flex flex-col w-full max-w-3xl max-h-[90vh] rounded-xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {previewDoc.documentType.name}
                </p>
                <p className="text-xs text-gray-400 truncate max-w-sm">{previewDoc.fileName}</p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={previewDoc.signedUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-brand-600 hover:text-brand-700 underline"
                >
                  Open in new tab
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center p-4">
              {isImage(previewDoc.mimeType) ? (
                <img
                  src={previewDoc.signedUrl}
                  alt={previewDoc.fileName}
                  className="max-w-full max-h-full object-contain rounded"
                />
              ) : previewDoc.mimeType === 'application/pdf' ? (
                <iframe
                  src={previewDoc.signedUrl}
                  title={previewDoc.fileName}
                  className="w-full h-[70vh] rounded border-0"
                />
              ) : (
                <div className="text-center py-12">
                  <p className="text-sm text-gray-500 mb-3">
                    Preview not available for this file type.
                  </p>
                  <a
                    href={previewDoc.signedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-brand-600 hover:text-brand-700 underline"
                  >
                    Download / Open file
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
